import React, { useEffect } from "react";
import _ from 'underscore';
import dayjs from 'dayjs';
import { Form, Input, Button, Table, message, DatePicker, Checkbox, Select } from 'antd';
import sortOrder from './utils/sortOrder.js';
import filter from './utils/filter.js';

const { Option } = Select;

const datePickerFormat = 'MMM Do YYYY'
const by = 'branchDivisionDepartment';

const ApprovalFindByBranchDivisionCostCenterForm = ({ requestDates, branchOptions, divisionOptions, costCenterOptions, ...props }) => {

  const { getFieldDecorator, getFieldsError, getFieldsValue } = props.form;

  useEffect(() => { // set form values
    const form = props.form;
    form.setFieldsValue({
      fromDate: requestDates.fromDate ? dayjs(requestDates.fromDate).startOf('day') : dayjs().startOf('day'),
      toDate: requestDates.toDate ? dayjs(requestDates.toDate).startOf('day') : dayjs().startOf('day'),
    });
  }, [requestDates, branchOptions, divisionOptions, costCenterOptions]);

  const invalidForm = () => {
    message.config({
      top: 150,
      duration: 5,
      maxCount: 5,
    });
    message.error('You have one or more invalid form inputs!');
  };

  const validateForm = () => { // validates the whole form
    const form = props.form;

    const fromDate = form.getFieldValue('fromDate') ? form.getFieldValue('fromDate').startOf('day').clone() : null;
    const toDate = form.getFieldValue('toDate') ? form.getFieldValue('toDate').startOf('day').clone() : null;
    const branch = form.getFieldValue('branch');
    const division = form.getFieldValue('division');
    const costCenter = form.getFieldValue('costCenter');

    let fromDateErrors = [];
    if (!fromDate) {
      fromDateErrors.push(new Error('Please input start date!'));
    }
    if (fromDate && toDate && toDate.isBefore(fromDate)) {
      fromDateErrors.push(new Error('Start date cannot occur after end date!'));
    }
    if (fromDateErrors.length > 0) {
      form.setFields({
        fromDate: {
          value: fromDate,
          errors: fromDateErrors,
        }
      });
    } else {
      form.setFields({
        fromDate: {
          value: fromDate,
        }
      });
    }

    let toDateErrors = [];
    if (!toDate) {
      toDateErrors.push(new Error('Please input end date!'));
    }
    if (toDate && fromDate && fromDate.isAfter(toDate)) {
      toDateErrors.push(new Error('End date cannot occur before start date!'));
    }
    if (toDateErrors.length > 0) {
      form.setFields({
        toDate: {
          value: toDate,
          errors: toDateErrors,
        }
      });
    } else {
      form.setFields({
        toDate: {
          value: toDate,
        }
      });
    }

    let branchErrors = [];
    if (!branch) {
      branchErrors.push(new Error('Please input branch!'));
    }
    if (branchErrors.length > 0) {
      form.setFields({
        branch: {
          value: branch,
          errors: branchErrors,
        }
      });
    } else {
      form.setFields({
        branch: {
          value: branch,
        }
      });
    }

    let divisionErrors = [];
    if (!division) {
      divisionErrors.push(new Error('Please input division!'));
    }
    if (divisionErrors.length > 0) {
      form.setFields({
        division: {
          value: division,
          errors: divisionErrors,
        }
      });
    } else {
      form.setFields({
        division: {
          value: division,
        }
      });
    }

    let costCenterErrors = [];
    if (!costCenter) {
      costCenterErrors.push(new Error('Please input cost center!'));
    }
    if (costCenterErrors.length > 0) {
      form.setFields({
        costCenter: {
          value: costCenter,
          errors: costCenterErrors,
        }
      });
    } else {
      form.setFields({
        costCenter: {
          value: costCenter,
        }
      });
    }
  }

  const handleBranchChange = (value) => {
    props.getDivisionOptions(value);
  }

  const handleDivisionChange = (value) => {
    props.getDepartmentOptions(value);
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    validateForm();
    const errors = {};
    // collect all fields currently in error state
    for (const [field, error] of Object.entries(getFieldsError())) {
      if (error) {
        errors[`${field}`] = error;
      }
    }
    if (Object.values(errors).length === 0) {
      let formValues = getFieldsValue();
      //
      console.log('Success! Received values of form: ', formValues)
      props.searchByBranchDivisionDepartment(formValues, by);
    } else {
      invalidForm();
      console.log('Invalid Form! ', errors);
    }
  }

  return (
    <Form
      onSubmit={(e) => handleSubmit(e)}>

      <Form.Item
        label="From Date"
        style={{ marginBottom: 5 }}
      >
        {getFieldDecorator('fromDate', {
          rules: [
            {
              required: true,
              message: 'Please input start date!'
            },
          ],
        })(
          <DatePicker
            name="fromDate"
            // showTime={{ format: datePickerFormat, use12Hours: true }}
            onChange={(e) => props.handleFromDateChange(e, validateForm)}
            allowClear={false}
            placeholder={'Select date and time'}
            format={datePickerFormat}
            size={'large'}
            styles={{
              popup: {
                root: {
                  zIndex: 2000
                }
              }
            }}
          />
        )}
      </Form.Item>

      <Form.Item
        label="To Date"
        style={{ marginBottom: 5 }}
      >
        {getFieldDecorator('toDate', {
          rules: [
            {
              required: true,
              message: 'Please input end date!'
            },
          ],
        })(
          <DatePicker
            name="toDate"
            // showTime={{ format: datePickerFormat, use12Hours: true }}
            onChange={(e) => props.handleToDateChange(e, validateForm)}
            allowClear={false}
            placeholder={'Select date and time'}
            format={datePickerFormat}
            size={'large'}
            styles={{
              popup: {
                root: {
                  zIndex: 2000
                }
              }
            }}
          />
        )}
      </Form.Item>

      {/* Branch/Division/Cost Center */}
      <Form.Item
        label="Branch"
        style={{ marginBottom: 5 }}
      >
        {getFieldDecorator('branch', {
          initialValue: null,
          rules: [{
            required: true, message: 'Please select a branch!',
          }],
        })(
          <Select
            name='branch'
            allowClear={false}
            style={{ width: 250 }}
            dropdownStyle={{ zIndex: 2000 }}
            onChange={(e) => handleBranchChange(e)}
          >
            {branchOptions.map((branchOption, index) => {
              return <Option key={'branchOption' + index} value={branchOption.id} >{branchOption.name}</Option>
            })}
          </Select>
        )}
      </Form.Item>

      <Form.Item
        label="Division"
        style={{ marginBottom: 5 }}
      >
        {getFieldDecorator('division', {
          initialValue: null,
          rules: [{
            required: true, message: 'Please select a division!',
          }],
        })(
          <Select
            name='division'
            allowClear={false}
            style={{ width: 250 }}
            dropdownStyle={{ zIndex: 2000 }}
            onChange={(e) => handleDivisionChange(e)}
          >
            {divisionOptions.map((divisionOption, index) => {
              return <Option key={'divisionOption' + index} value={divisionOption.id} >{divisionOption.name}</Option>
            })}
          </Select>
        )}
      </Form.Item>

      <Form.Item
        label="Cost Center"
        style={{ marginBottom: 5 }}
      >
        {getFieldDecorator('costCenter', {
          initialValue: null,
          rules: [{
            required: true, message: 'Please select a cost center!',
          }],
        })(
          <Select
            name='costCenter'
            allowClear={false}
            style={{ width: 250 }}
            dropdownStyle={{ zIndex: 2000 }}
          >
            {costCenterOptions.map((costCenterOption, index) => {
              return <Option key={'costCenterOption' + index} value={costCenterOption.id} >{costCenterOption.name}</Option>
            })}
          </Select>
        )}
      </Form.Item>

      <Form.Item
        label="Filter"
        style={{ marginBottom: 5 }}
      >
        {getFieldDecorator('filter', {
          initialValue: filter[0].value || null,
          rules: [{
            required: true, message: 'Please input a status!',
          }],
        })(
          <Select
            name='filter'
            allowClear={false}
            style={{ width: 250 }}
            dropdownStyle={{ zIndex: 2000 }}
          >
            {filter.map((filterOption, index) => {
              return <Option key={'filterOption' + index} value={filterOption.value} >{filterOption.name}</Option>
            })}
          </Select>
        )}
      </Form.Item>

      <Form.Item
        label="Sort Order"
        style={{ marginBottom: 5 }}
      >
        {getFieldDecorator('sortOrder', {
          initialValue: sortOrder[0].value || null,
          rules: [{
            required: true, message: 'Please input a sort order!',
          }],
        })(
          <Select
            name='sortOrder'
            allowClear={false}
            style={{ width: 250 }}
            dropdownStyle={{ zIndex: 2000 }}
          >
            {sortOrder.map((sortOption, index) => {
              return <Option key={'sortOption' + index} value={sortOption.value} >{sortOption.name}</Option>
            })}
          </Select>
        )}
      </Form.Item>

      <Form.Item
        style={{ marginBottom: 24 }}
      >
        {getFieldDecorator('sortByCostObject', {
          initialValue: false,
        })(
          <Checkbox name='sortByCostObject'>
            Sort By Cost Object
          </Checkbox>
        )}
      </Form.Item>

      <Form.Item>
        <Button type="primary" htmlType='submit'>
          Submit
        </Button>
      </Form.Item>

    </Form >
  );
}

export default ApprovalFindByBranchDivisionCostCenterForm;
